import { inject, Injectable, Signal } from '@angular/core';
import {
  DEFAULT_SPORT_CONFIGS,
  SportConfig,
  SportLabels,
  SportOption,
  SportUIFlags,
} from '@models';
import { map, Observable, combineLatest } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { TenantFeaturesService } from './tenant-features.service';
import { SportContextService } from './sport-context.service';

/**
 * Provides sport-specific configuration, labels, and feature flags derived
 * from the active tenant's `sportConfig` field.
 *
 * Inject this service in any component that needs to:
 *  - Display sport-adapted entity names ("Remero" instead of "Jugador")
 *  - Show/hide UI sections based on sport rules (positions, race format, etc.)
 *  - Populate dropdowns with sport-specific options (scopes, modalities)
 *
 * Falls back to `fronton` config while TenantConfig is loading (null).
 */
@Injectable({ providedIn: 'root' })
export class SportConfigService {
  private static readonly FALLBACK = DEFAULT_SPORT_CONFIGS[
    'fronton'
  ] as SportConfig;

  /** The full SportConfig for the current tenant. Falls back to fronton while loading. */
  readonly config$: Observable<SportConfig>;

  /** Sport-specific UI labels. */
  readonly labels$: Observable<SportLabels>;

  /** Sport-specific UI feature flags. */
  readonly flags$: Observable<SportUIFlags>;

  /** Scope options for competition/team dropdowns. */
  readonly scopeOptions$: Observable<SportOption[]>;

  /** Modality options for competition dropdowns. */
  readonly modalityOptions$: Observable<SportOption[]>;

  /** Competition type labels for this sport. */
  readonly competitionTypeLabels$: Observable<Record<string, string>>;

  /**
   * Tenant-wide default for round-robin generation: whether the team in the
   * last position of the list starts playing at home. Read-only here; each
   * season keeps its own copy (see `SeasonLeague`/`SeasonCup`).
   */
  readonly lastPositionStartsHome$: Observable<boolean>;

  /** Signal: full SportConfig — use in templates to avoid async pipe boilerplate. */
  readonly configSignal: Signal<SportConfig>;

  /** Signal: sport-specific UI labels — use in templates. */
  readonly labelsSignal: Signal<SportLabels>;

  constructor(
    private readonly features: TenantFeaturesService,
    private readonly sportContext: SportContextService,
  ) {
    this.config$ = combineLatest([
      this.features.tenantConfig$,
      this.sportContext.activeSport$,
    ]).pipe(
      map(([cfg, activeSport]) => {
        const s = cfg?.sportConfig;
        // Base config — merge with FALLBACK so partial Firestore objects always yield a complete config
        const base: SportConfig = s
          ? ({ ...SportConfigService.FALLBACK, ...s } as SportConfig)
          : SportConfigService.FALLBACK;
        // For multi-sport clubs, override with the per-sport config when available
        if (activeSport && cfg) {
          return this.sportContext.getSportConfig(activeSport, base);
        }
        return base;
      }),
    );

    this.labels$ = this.config$.pipe(
      map((c) => c.labels ?? SportConfigService.FALLBACK.labels),
    );

    this.flags$ = this.config$.pipe(
      map(
        (c): SportUIFlags => ({
          hasPositions: c.hasPositions,
          isRaceFormat: c.isRaceFormat,
          showGameScore: !c.isRaceFormat,
          hasLicensePositions: c.hasPositions,
          hasTransfers: !c.isRaceFormat,
        }),
      ),
    );

    this.scopeOptions$ = this.config$.pipe(
      map(
        (c) => c.availableScopes ?? SportConfigService.FALLBACK.availableScopes,
      ),
    );
    this.modalityOptions$ = this.config$.pipe(
      map(
        (c) =>
          c.availableModalities ??
          SportConfigService.FALLBACK.availableModalities,
      ),
    );
    this.competitionTypeLabels$ = this.config$.pipe(
      map(
        (c) =>
          c.competitionTypeLabels ??
          SportConfigService.FALLBACK.competitionTypeLabels,
      ),
    );

    this.lastPositionStartsHome$ = this.config$.pipe(
      map((c) => c.lastPositionStartsHome ?? false),
    );

    this.configSignal = toSignal(this.config$, {
      initialValue: SportConfigService.FALLBACK,
    });
    this.labelsSignal = toSignal(this.labels$, {
      initialValue: SportConfigService.FALLBACK.labels,
    });
  }

  /**
   * Synchronous snapshot helpers — use when subscribing is not practical
   * (e.g. building arrays in constructor). Reads from TenantFeaturesService
   * snapshot; returns fronton defaults if config not yet loaded.
   */
  getLabels(sportConfig: SportConfig | null | undefined): SportLabels {
    return sportConfig?.labels ?? SportConfigService.FALLBACK.labels;
  }

  getFlags(sportConfig: SportConfig | null | undefined): SportUIFlags {
    const c = sportConfig ?? SportConfigService.FALLBACK;
    return {
      hasPositions: c.hasPositions,
      isRaceFormat: c.isRaceFormat,
      showGameScore: !c.isRaceFormat,
      hasLicensePositions: c.hasPositions,
      hasTransfers: !c.isRaceFormat,
    };
  }

  getScopeOptions(sportConfig: SportConfig | null | undefined): SportOption[] {
    return (
      sportConfig?.availableScopes ??
      SportConfigService.FALLBACK.availableScopes
    );
  }

  getModalityOptions(
    sportConfig: SportConfig | null | undefined,
  ): SportOption[] {
    return (
      sportConfig?.availableModalities ??
      SportConfigService.FALLBACK.availableModalities
    );
  }

  getCompetitionTypeLabels(
    sportConfig: SportConfig | null | undefined,
  ): Record<string, string> {
    return (
      sportConfig?.competitionTypeLabels ??
      SportConfigService.FALLBACK.competitionTypeLabels
    );
  }

  /**
   * Tenant default for round-robin generation. Absent in Firestore → `false`,
   * i.e. the historical behaviour (pivot starts away).
   */
  getLastPositionStartsHome(
    sportConfig: SportConfig | null | undefined,
  ): boolean {
    return sportConfig?.lastPositionStartsHome ?? false;
  }
}
